import AddCircleIcon from '@mui/icons-material/AddCircle';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';

import React, { useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { IsActiveEnum } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { Button } from '_lib/components/Button/Button';
import { Card } from '_lib/components/Card/Card';
import { CardContent } from '_lib/components/CardContent/CardContent';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { Search } from '_lib/components/Search/Search';
import { activeEnumOptions, SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';

import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';

import { AppDrawerState, AppRouteContext } from '../App/app.types';
import { AuthContext } from '../auth/authContext';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';

type AccountListProps = {
  appDrawerState?: AppDrawerState;
};

const AccountList = (props: AccountListProps) => {
  const { appDrawerState } = props;

  const navigate = useNavigate();

  const api = useContext(ApiContext);
  const { callCenterCacheResource } = useContext(AuthContext);
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const { params, setParam, searchParams } = useQueryParams({
    searchWords: { defaultValue: '' },
    isActive: { defaultValue: IsActiveEnum.True, normalize: normalizers.enum(Object.values(IsActiveEnum)) }
  });

  const accountsQuery = api.accounts.useUnpagedList(
    {
      params: {
        ...params,
        callCenterId: cachedCallCenterQuery.data?.id || ''
      }
    },
    { enabled: !!cachedCallCenterQuery.data?.id }
  );

  if (accountsQuery.error) {
    return <RouteErrorBoundary appBarTitle='Accounts' errorTitle='Error Fetching Accounts' />;
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='Accounts' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <ActionBar
        actions={
          <Button
            disableFocusRipple
            text='Add Account'
            variant='text'
            onClick={() => {
              navigate(`${getFullAppRoutePath(appRoutePaths.Account)}/new`, {
                state: { goBack: getGoBackState(appRoutePaths.Account, searchParams) }
              });
            }}
            startIcon={<AddCircleIcon />}
            sx={{ textTransform: 'none' }}
          />
        }
      />
      <Divider />
      <Stack direction='row' padding={2}>
        <SelectFilter
          label='Active/Inactive'
          initialKey={params.isActive}
          options={activeEnumOptions}
          onChange={(selectedKey: string) => setParam('isActive', selectedKey)}
          sx={{ width: '125px' }}
        />
        <Search initialValue={params.searchWords} onSearch={(searchWords) => setParam('searchWords', searchWords)} />
      </Stack>
      <Divider />
      <ScrollableContentWrapper isSavingOrLoading={accountsQuery.isFetching}>
        {!accountsQuery.isFetching && !accountsQuery.data?.length && (
          <Card variant='outlined'>
            <CardContent>
              <Typography text='No Items were found.' />
            </CardContent>
          </Card>
        )}
        {!!accountsQuery.data?.length && (
          <List sx={{ padding: 0 }}>
            {accountsQuery.data.map((account) => (
              <ListItem key={account.id} divider disablePadding>
                <LinkListItemButton
                  href={`${getFullAppRoutePath(appRoutePaths.Account)}/${account.id}`}
                  onClick={() => {
                    navigate(`${account.id}`, {
                      state: { goBack: getGoBackState(appRoutePaths.Account, searchParams) }
                    });
                  }}>
                  <ListItemAvatar>
                    <Avatar>
                      <HolidayVillageIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <>
                        <Typography component='span' text={account.name} />
                        {!account.isActive && (
                          <Typography component='span' text='INACTIVE' color='error' sx={{ paddingLeft: 1 }} />
                        )}
                      </>
                    }
                  />
                </LinkListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </ScrollableContentWrapper>
    </FlexBox>
  );
};

export const AccountListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  return <AccountList appDrawerState={appDrawerState} />;
};
